import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Users, BarChart3, Zap, Clock, DollarSign, Shield, TrendingUp, Layers } from 'lucide-react';

const ADMIN_KEY = 'forge-admin-dev';

export default function AdminPage() {
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    const load = async () => {
      try {
        const [m, u] = await Promise.all([
          api.getAdminMetrics(ADMIN_KEY),
          api.getAdminUsers(ADMIN_KEY, 50),
        ]);
        setMetrics(m);
        setUsers(u);
      } catch (err) {
        toast.error('Failed to load admin data');
      } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in-up opacity-0" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-8">
        <Shield className="w-6 h-6 text-[#CDFE65]" />
        <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>Admin Dashboard</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['overview', 'users'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${tab === t ? 'bg-[#CDFE65] text-black' : 'bg-white/5 text-white/40 hover:text-white'}`} data-testid={`admin-tab-${t}`}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'overview' && metrics && (
        <div className="space-y-6">
          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-metrics">
            <MetricCard icon={Users} label="Total Users" value={metrics.users?.total || 0} sub={`${metrics.users?.paying || 0} paying`} color="#CDFE65" />
            <MetricCard icon={Zap} label="Total Generations" value={metrics.generations?.total || 0} sub={`${metrics.generations?.today || 0} today`} color="#06B6D4" />
            <MetricCard icon={DollarSign} label="Total Cost" value={`$${metrics.cost?.total_usd || 0}`} sub="platform costs" color="#F59E0B" />
            <MetricCard icon={TrendingUp} label="Waitlist" value={metrics.waitlist || 0} sub={`${metrics.teams || 0} teams`} color="#7C3AED" />
          </div>

          {/* Generation Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-mono uppercase tracking-wider text-white/40 mb-4">Generation Status</h3>
              <div className="space-y-3">
                <StatusBar label="Completed" value={metrics.generations?.completed || 0} total={metrics.generations?.total || 1} color="#CDFE65" />
                <StatusBar label="Failed" value={metrics.generations?.failed || 0} total={metrics.generations?.total || 1} color="#EF4444" />
                <StatusBar label="Processing" value={metrics.generations?.processing || 0} total={metrics.generations?.total || 1} color="#F59E0B" />
              </div>
            </div>
            <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-mono uppercase tracking-wider text-white/40 mb-4">Plan Distribution</h3>
              <div className="space-y-3">
                {Object.entries(metrics.users?.plan_distribution || {}).map(([plan, count]) => (
                  <StatusBar key={plan} label={plan} value={count} total={metrics.users?.total || 1} color={plan === 'free' ? '#A1A1AA' : plan === 'starter' ? '#06B6D4' : plan === 'growth' ? '#CDFE65' : '#7C3AED'} />
                ))}
              </div>
            </div>
            <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-mono uppercase tracking-wider text-white/40 mb-4">This Month</h3>
              <div className="space-y-4">
                <div><p className="text-xs text-white/30">Generations</p><p className="text-2xl font-black" style={{ fontFamily: 'Chivo' }}>{metrics.generations?.this_month || 0}</p></div>
                <div><p className="text-xs text-white/30">Comparisons</p><p className="text-2xl font-black" style={{ fontFamily: 'Chivo' }}>{metrics.comparisons || 0}</p></div>
              </div>
            </div>
          </div>

          {/* Recent Users */}
          <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
            <h3 className="text-sm font-mono uppercase tracking-wider text-white/40 mb-4">Recent Signups</h3>
            <div className="space-y-2">
              {(metrics.users?.recent || []).map(u => (
                <div key={u.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#CDFE65]/20 flex items-center justify-center text-[#CDFE65] text-xs font-bold">{(u.name || u.email)[0].toUpperCase()}</div>
                    <div>
                      <p className="text-sm font-medium">{u.name || 'Anonymous'}</p>
                      <p className="text-xs text-white/30 font-mono">{u.email}</p>
                    </div>
                  </div>
                  <span className="text-xs px-2 py-0.5 bg-white/5 rounded font-mono">{u.plan}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'users' && (
        <div className="bg-[#0F0F11] border border-white/5 rounded-xl overflow-hidden" data-testid="admin-users-table">
          <table className="w-full">
            <thead><tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 text-xs font-mono uppercase text-white/40">User</th>
              <th className="text-left px-4 py-3 text-xs font-mono uppercase text-white/40">Plan</th>
              <th className="text-left px-4 py-3 text-xs font-mono uppercase text-white/40">Usage</th>
              <th className="text-left px-4 py-3 text-xs font-mono uppercase text-white/40">Joined</th>
            </tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium">{u.name || 'Anonymous'}</p>
                    <p className="text-xs text-white/30 font-mono">{u.email}</p>
                  </td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${u.plan === 'free' ? 'bg-white/5 text-white/40' : 'bg-[#CDFE65]/10 text-[#CDFE65]'}`}>{u.plan}</span></td>
                  <td className="px-4 py-3 text-sm font-mono text-white/60">{u.generations_used}/{u.generations_limit}</td>
                  <td className="px-4 py-3 text-xs text-white/30 font-mono">{u.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5 card-hover">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-wider text-white/40">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="text-2xl font-black" style={{ fontFamily: 'Chivo' }}>{value}</div>
      <p className="text-xs text-white/30 mt-1">{sub}</p>
    </div>
  );
}

function StatusBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs capitalize text-white/60">{label}</span>
        <span className="text-xs font-mono text-white/40">{value} ({pct}%)</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
